import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CookieConsent } from "./components/analytics/CookieConsent";
import { capturePageview } from "./analytics/posthog";

import Landing from "./pages/Landing";
import LegalPage from "./pages/LegalPage";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Audit from "./pages/Audit";
import AppShell from "./components/layout/AppShell";
import ArchitectHome from "./pages/ArchitectHome";
import Dashboard from "./pages/Dashboard";
import MapPage from "./pages/MapPage";
import PlanPage from "./pages/PlanPage";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Systems from "./pages/Systems";
import SystemDetailPage from "./pages/SystemDetailPage";
import Team from "./pages/Team";
import Billing from "./pages/Billing";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian">
      <div className="flex items-center gap-3 text-slate-400">
        <div className="h-5 w-5 rounded-full border-2 border-amber-500 border-t-transparent spin-slow" />
        <span className="mono-label">CARGANDO</span>
      </div>
    </div>
  );
}

function Protected({ children, requireOnboarding = true }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/auth" replace />;
  if (requireOnboarding && !user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function PageviewTracker() {
  const location = useLocation();
  useEffect(() => {
    // Captures a $pageview on first load (if consent granted) and on every SPA
    // route change. No-op until consent; module-level dedup prevents duplicates.
    capturePageview();
  }, [location.pathname, location.search]);
  return null;
}

function Router() {
  const location = useLocation();


  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/cookies" element={<LegalPage doc="cookies" />} />
      <Route path="/legal" element={<LegalPage doc="legal" />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Protected requireOnboarding={false}><Onboarding /></Protected>} />
      <Route path="/audit" element={<Protected><Audit /></Protected>} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<ArchitectHome />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="map" element={<MapPage />} />
        <Route path="plan" element={<PlanPage />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="systems" element={<Systems />} />
        <Route path="systems/:id" element={<SystemDetailPage />} />
        <Route path="team" element={<Team />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageviewTracker />
        <Router />
        <Toaster position="top-right" theme="dark" />
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}
